import { useState, useCallback } from 'react';
import { TERMINAL_DATA } from '../constants/terminalConstants';
import { EW3035_ADVANCED_SETTINGS_DEFAULTS } from '../machines/EW-3035+L';

export const useCableState = () => {
  const [cables, setCables] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingMultipolarIndex, setEditingMultipolarIndex] = useState(-1);
  const [cableMode, setCableMode] = useState('standard'); // 'standard' | 'multipolar'

  const [currentCable, setCurrentCable] = useState({
    siglaturaA: '',
    siglaturaB: '',
    useSeparateSiglature: false,
    gruppo: '',
    colore: 'ORANGE',
    sezione: 0.5,
    lunghezza: 200,
    terminaleA: 'PSW',
    terminaleB: 'PSW',
    collegamentoA: '',
    collegamentoB: '',
    guaina: null // ID della guaina associata (opzionale)
  });

  // Stato per le guaine disponibili
  const [availableSheaths, setAvailableSheaths] = useState([]);

  // Nuovo stato per cavi multipolari
  const [currentMultipolarCable, setCurrentMultipolarCable] = useState({
    gruppo: '',
    lunghezza: 200,
    nomeCavo: '',
    poli: [
      {
        id: Date.now(),
        colore: 'ORANGE',
        sezione: 0.5,
        siglaturaA: '',
        siglaturaB: '',
        lunghezzaA: 200,
        lunghezzaB: 200,
        terminaleA: 'PSW',
        terminaleB: 'PSW',
        collegamentoA: '',
        collegamentoB: ''
      }
    ]
  });

  const [settings, setSettings] = useState({
    quantitaCablaggi: 1,
    commessaId: '',
    commessaDate: '',
    marcaCavi: 1,
    enableSeparateSingleCableSiglature: false,
    marcaturePerCollegamento: false,
    lingua: 'it',
    lunghezzaPreview: 350,
    useAWG: false,
    ...EW3035_ADVANCED_SETTINGS_DEFAULTS,
  });

  // Nuovi stati generali (estraiamo da settings per passargli direttamente)
  const [fileName, setFileName] = useState('');
  const [revision, setRevision] = useState(0);
  const [schemaElettrico, setSchemaElettrico] = useState('');
  const [installazione, setInstallazione] = useState('');
  const [groupTitles, setGroupTitles] = useState({});
  const [listSortMode, setListSortMode] = useState('numerazione');

  // Stati aggiuntivi per compatibilità con App.js
  const [language, setLanguage] = useState('it');
  const [unitType, setUnitType] = useState('metrico');
  const [multipolarCables, setMultipolarCables] = useState([]);
  const [terminali, setTerminali] = useState(JSON.parse(JSON.stringify(TERMINAL_DATA)));

  const handleInputChange = useCallback((field, value) => {
    setCurrentCable(prev => ({ ...prev, [field]: value }));
  }, []);

  // Le sezioni sono SEMPRE memorizzate in mm². Il passaggio tra mm e AWG è
  // puramente visivo (gestito da formatSezione/SectionSelector), quindi NON
  // deve mutare i dati dei cavi: qui aggiorniamo solo le impostazioni.
  const updateSettings = useCallback((updaterOrPartial) => {
    setSettings(prevSettings => {
      const incomingUpdate =
        typeof updaterOrPartial === 'function'
          ? updaterOrPartial(prevSettings)
          : updaterOrPartial;
      return { ...prevSettings, ...(incomingUpdate || {}) };
    });
  }, []);

  const resetCurrentCable = useCallback(() => {
    setCurrentCable({
      siglaturaA: '',
      siglaturaB: '',
      useSeparateSiglature: false,
      gruppo: '',
      colore: 'ORANGE',
      sezione: 0.5,
      lunghezza: 200,
      terminaleA: 'PSW',
      terminaleB: 'PSW',
      collegamentoA: '',
      collegamentoB: '',
      guaina: null
    });
  }, []);

  const resetCurrentMultipolarCable = useCallback(() => {
    setCurrentMultipolarCable({
      gruppo: '',
      lunghezza: 200,
      nomeCavo: '',
      poli: [
        {
          id: Date.now(),
          colore: 'ORANGE',
          sezione: 0.5,
          siglaturaA: '',
          siglaturaB: '',
          lunghezzaA: 200,
          lunghezzaB: 200,
          terminaleA: 'PSW',
          terminaleB: 'PSW',
          collegamentoA: '',
          collegamentoB: ''
        }
      ]
    });
  }, []);

  const addMultipolarCable = useCallback((cable) => {
    const newCable = {
      ...cable,
      originalIndex: multipolarCables.length,
      poli: cable.poli || []
    };
    setMultipolarCables(prev => [...prev, newCable]);
  }, [multipolarCables.length]);

  const removeMultipolarCable = useCallback((index) => {
    setMultipolarCables(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearMultipolarCables = useCallback(() => {
    setMultipolarCables([]);
  }, []);

  // Funzioni per gestione poli nel cavo multipolare
  const addPolo = useCallback(() => {
    const newPolo = {
      id: Date.now(),
      colore: 'ORANGE',
      sezione: 0.5,
      siglaturaA: '',
      siglaturaB: '',
      lunghezzaA: currentMultipolarCable.lunghezza || 200,
      lunghezzaB: currentMultipolarCable.lunghezza || 200,
      terminaleA: 'PSW',
      terminaleB: 'PSW',
      collegamentoA: '',
      collegamentoB: ''
    };
    setCurrentMultipolarCable(prev => ({
      ...prev,
      poli: [...prev.poli, newPolo]
    }));
  }, [currentMultipolarCable.lunghezza]);

  const removePolo = useCallback((poloId) => {
    setCurrentMultipolarCable(prev => ({
      ...prev,
      poli: prev.poli.filter(p => p.id !== poloId)
    }));
  }, []);

  const updatePolo = useCallback((poloId, field, value) => {
    setCurrentMultipolarCable(prev => ({
      ...prev,
      poli: prev.poli.map(p => p.id === poloId ? { ...p, [field]: value } : p)
    }));
  }, []);

  const duplicatePolo = useCallback((poloId) => {
    setCurrentMultipolarCable(prev => {
      const toDup = prev.poli.find(p => p.id === poloId);
      if (!toDup) return prev;
      const copy = { ...toDup, id: Date.now() };
      const idx = prev.poli.findIndex(p => p.id === poloId);
      const newPoli = [...prev.poli];
      newPoli.splice(idx + 1, 0, copy);
      return { ...prev, poli: newPoli };
    });
  }, []);

  // Restituisci lo stato e le azioni nel formato che App.js si aspetta
  return {
    state: {
      // Stati principali
      cables,
      currentCable,
      currentMultipolarCable,
      settings: {
        ...settings,
        fileName,
        revision,
        schemaElettrico,
        installazione,
        groupTitles,
        listSortMode, // Espongo listSortMode
        terminal_data: terminali, // PROPAGATE terminal overrides to all components using settings!
      },
      editingIndex,
      editingMultipolarIndex,
      cableMode,
      
      // Stati per compatibilità con App.js
      language,
      unitType,
      multipolarCables,
      terminali,
      availableSheaths
    },
    actions: {
      // Setters principali
      setCables,
      setCurrentCable,
      setCurrentMultipolarCable,
      setSettings: updateSettings,
      setEditingIndex,
      setEditingMultipolarIndex,
      setCableMode,
      setTerminali,
      setFileName,
      setRevision,
      setSchemaElettrico,
      setInstallazione,
      setGroupTitles,
      setListSortMode, // Espongo setListSortMode
      
      // Setters per compatibilità
      setLanguage,
      setUnitType,
      
      // Funzioni helper
      handleInputChange,
      resetCurrentCable,
      resetCurrentMultipolarCable,
      
      // Funzioni per multipolari
      addMultipolarCable,
      removeMultipolarCable,
      clearMultipolarCables
      , addPolo, removePolo, updatePolo, duplicatePolo,
      
      // Funzioni per guaine
      setAvailableSheaths
    }
  };
};
